import React, { useEffect, useState, useContext, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../pages.css';
import {
  MButton,
  MTransferList,
  LoadingContext,
  M_StateActions,
  MHeader,
  MMessageBoard
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from '../../components/Forms/DynamicForm';

// ==========================================================================================================================================
// Helpers & Rules
// ==========================================================================================================================================

/**
 * Converts a string to camelCase (used to build DTO payload keys from Option.Header/Name).
 */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

// =================================================================================================================================================
// Component
// =================================================================================================================================================
export default function UserConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);

  // One-shot globals from the table (operation + selected row)
  const globalOperation = M_State.find((obj) => obj.key === 'operation')?.value;
  const globalLine = M_State.find((obj) => obj.key === 'UserLine')?.value;

  // =====================================================================================================================================-
  // Per-tab state (kept isolated by selectedTab id key)
  // =====================================================================================================================================-
  const [id, setId] = useState(M_State.find((obj) => obj.key === 'selectedTab')?.value);
  const [operation, setOperation] = useState(globalOperation ?? 'Error');
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});
  const [userInfo, setUserInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);
  const [userId, setUserId] = useState(sessionLine?.Id);

  // Transfer lists state
  const [userProfiles, setUserProfiles] = useState([]);  // [{Id, Name}]
  const [userAccounts, setUserAccounts] = useState([]);  // [{Id, Name}] (create/view/edit)

  // DF schema + raw options
  const [formSchema, setFormSchema] = useState([]);
  const [allOptions, setAllOptions] = useState([]);

  // Reference datasets (for transfer lists)
  const [profilesRef, setProfilesRef] = useState([]);
  const [accountsRef, setAccountsRef] = useState([]);

  // DF imperative ref
  const formRef = useRef(null);

  const isViewMode = (operation || '').toLowerCase() === 'view';
  const isCreateMode = operation === 'Create';
  const isEditMode = operation === 'Edit';

  // Services
  const svcUsers = useMemo(() => new BufferService('Users', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcCreate = useMemo(() => new BufferService('Users/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcUpdate = useMemo(() => new BufferService('Users/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  const svcProfiles = useMemo(() => new BufferService('Profiles', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcAccounts = useMemo(() => new BufferService('Accounts', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  // =====================================================================================================================================
  // Self re-auth flow after updating own user
  // =====================================================================================================================================
  const [showSelfReauthConfirm, setShowSelfReauthConfirm] = useState(false);
  const [pendingSelfUpdatePayload, setPendingSelfUpdatePayload] = useState(null);

  // Hide internal/identity fields that must never appear on forms
  const INTERNAL_USER_FIELDS = new Set([
    'PasswordHash',
    'PasswordSalt',
    'SecurityStamp',
    'ConcurrencyStamp',
    'TwoFactorEnabled',
    'LockoutEnd',
    'LockoutEnabled',
    'AccessFailedCount'
  ]);

  /**
   * Include rule for Create:
   * - exclude keys & audit
   * - exclude fields hidden in edit (backend hint)
   * - exclude Inactive (system-managed on Create)
   * - exclude internal identity fields (PasswordHash,...)
   * - exclude any FK fields (we do not resolve FKs inside DynamicForm on this page for create)
   */
  const includeInCreate = (opt) => {
    if (!opt) return false;
    if (opt.IsKey || opt.IsPrimaryKey) return false;
    const name = opt.Name || '';
    if (name === 'CreatedAt' || name === 'UpdatedAt' || name === 'CreatedBy' || name === 'UpdatedBy') return false;
    if (opt.IsHiddenEdit) return false;
    if (name === 'Inactive') return false;
    if (INTERNAL_USER_FIELDS.has(name)) return false;
    // Allow DefaultAccount FK specifically; keep all other FKs out of DF.
    if (opt.IsForeignKey && name !== 'DefaultAccount') return false;
    return true;
  };

  /**
   * Include rule for Edit:
   * - exclude keys & audit
   * - exclude fields hidden in edit
   * - exclude internal identity fields (PasswordHash,...)
   * - exclude FK fields in general, except DefaultAccountId / DefaultAccount which we intentionally allow
   *   so that DefaultAccountId can be edited together with Accounts.
   */
  const includeInEdit = (opt) => {
    if (!opt) return false;
    if (opt.IsKey || opt.IsPrimaryKey) return false;
    const name = opt.Name || '';
    if (name === 'CreatedAt' || name === 'UpdatedAt' || name === 'CreatedBy' || name === 'UpdatedBy') return false;
    if (name === 'UserName' || name === 'Email') return false;
    if (opt.IsHiddenEdit) return false;
    if (INTERNAL_USER_FIELDS.has(name)) return false;
    // Allow only DefaultAccount / DefaultAccountId FK, ignore other FKs for DF.
    if (opt.IsForeignKey && name !== 'DefaultAccount' && name !== 'DefaultAccountId') return false;
    return true;
  };

  /**
   * Include rule for View:
   * - exclude keys & audit
   * - respect IsHiddenList
   * - allow FKs here because we are not editing, just viewing (DynamicForm will show raw values if any)
   */
  const includeInView = (opt) => {
    if (!opt) return false;
    if (opt.IsKey || opt.IsPrimaryKey) return false;
    const name = opt.Name || '';
    if (name === 'CreatedAt' || name === 'UpdatedAt' || name === 'CreatedBy' || name === 'UpdatedBy') return false;
    if (opt.IsHiddenList) return false;
    return true;
  };


  const getLoggedUserIdFromStorage = () => {
    const raw = localStorage.getItem('userId');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const forceReAuthentication = () => {
    try {
      const lng = localStorage.getItem('lng');
      localStorage.clear();
      if (lng) localStorage.setItem('lng', lng);
    } finally {
      window.location.replace('/login');
    }
  };

  const confirmSelfReauthAndSave = async () => {
    try {
      if (!pendingSelfUpdatePayload) {
        setShowSelfReauthConfirm(false);
        return;
      }
      ctx.setIsLoad(true);

      const res = await svcUpdate.update(userId, pendingSelfUpdatePayload);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
        setShowSelfReauthConfirm(false);
        setPendingSelfUpdatePayload(null);
        return;
      }

      toastMessages('success', '', t('UserUpdated'));
      ctx.toastMessages?.('success', '', t('RefreshingSessionToApplyChanges'));
      forceReAuthentication();
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
    } finally {
      ctx.setIsLoad(false);
      setPendingSelfUpdatePayload(null);
    }
  };

  // =====================================================================================================================================
  // Boot: restore snapshot then fetch Options (and Details if needed)
  // =====================================================================================================================================
  useEffect(() => {
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

    const snapInfo = pick('userInfo');
    const snapIF = pick('isFirst');
    const snapOP = pick('operation');
    const snapSL = pick('sessionLine');
    const snapSchema = pick('formSchema');
    const snapProfiles = pick('userProfiles');
    const snapAccounts = pick('userAccounts');

    if (snapInfo) setUserInfo(snapInfo);
    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) {
      setSessionLine(seedLine);
      if (seedLine.Id) setUserId(seedLine.Id);
    }
    if (Array.isArray(snapSchema)) setFormSchema(snapSchema);
    if (Array.isArray(snapProfiles)) setUserProfiles(snapProfiles);
    if (Array.isArray(snapAccounts)) setUserAccounts(snapAccounts);

    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'UserLine' }));

    fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
      await Promise.all([loadProfilesRef(), loadAccountsRef()]);
      if (seedOperation === 'Create') {
        ctx.setIsLoad(false);
      } else if (seedOperation && seedLine?.Id) {
        await fetchDetails(seedLine?.Id);
        ctx.setIsLoad(false);
      } else {
        ctx.setIsLoad(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================================================================================================
  // Persist per-tab snapshot
  // =====================================================================================================================================
  useEffect(() => {
    if (!id) return;

    const dfValues = formRef.current?.getValues?.() || {};
    const infoToPersist =
      (operation || '').toLowerCase() === 'view'
        ? userInfo
        : { ...userInfo, ...dfValues };

    dispatch(
      M_StateActions.addM_State({
        key: id,
        value: [
          { key: 'userInfo', value: infoToPersist },
          { key: 'isFirst', value: isFirst },
          { key: 'operation', value: operation },
          { key: 'sessionLine', value: sessionLine },
          { key: 'formSchema', value: formSchema },
          { key: 'userProfiles', value: userProfiles },
          { key: 'userAccounts', value: userAccounts }
        ]
      })
    );
  }, [id, userInfo, isFirst, operation, sessionLine, formSchema, userProfiles, userAccounts, dispatch]);

  // =====================================================================================================================================
  // Track tab switch -> restore / refetch
  // =====================================================================================================================================
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  // =====================================================================================================================================
  // Language change -> rebuild schema/options
  // =====================================================================================================================================
  useEffect(() => {
    if (!operation) return;
    fetchOptions(operation, sessionLine, { skipDetails: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // =====================================================================================================================================
  // Restore snapshot for a tab id (or refetch when missing)
  // =====================================================================================================================================
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);
    const data = M_State.find((obj) => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;

      setUserInfo(pick('userInfo') || {});
      setIsFirst(pick('isFirst') ?? false);
      setOperation(pick('operation') ?? 'Error');

      const tempSL = pick('sessionLine') || {};
      setSessionLine(tempSL);
      if (tempSL?.Id) setUserId(tempSL.Id);

      const tempSchema = pick('formSchema');
      if (Array.isArray(tempSchema)) setFormSchema(tempSchema);

      setUserProfiles(pick('userProfiles') || []);
      setUserAccounts(pick('userAccounts') || []);
      ctx.setIsLoad(false);
      return;
    }

    fetchOptions(operation, sessionLine, { skipDetails: true }).finally(async () => {
      await Promise.all([loadProfilesRef(), loadAccountsRef()]);
      if (operation !== 'Create' && sessionLine?.Id) await fetchDetails(sessionLine?.Id);
      ctx.setIsLoad(false);
    });
  };

  // =====================================================================================================================
  // Fetch Options -> filter by mode (override includeInCreate/Edit/View).
  // IMPORTANT: FK fields are excluded in include rules so we do not need to resolve FKs for DynamicForm,
  // except for DefaultAccountId on Edit where we intentionally allow it.
  // =====================================================================================================================
  const fetchOptions = async (op, line, { skipDetails } = { skipDetails: false }) => {
    ctx.setIsLoad(true);
    try {
      const res = await svcUsers.options();
      if (isFailurePayload(res) || res?.name === 'AxiosError') {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }

      const options = Array.isArray(res) ? res : (res || []);
      setAllOptions(options);

      const mode = op || operation || 'View';
      const schema =
        mode === 'Create'
          ? options.filter(includeInCreate)
          : mode === 'Edit'
            ? options.filter(includeInEdit)
            : options.filter(includeInView);

      setFormSchema(schema);

      // NOTE: FK datasets are intentionally NOT loaded here (FK resolver removed from DF).
      // Transfer-related datasets (Profiles/Accounts) are handled by their own loaders.

      if (!skipDetails && mode !== 'Create' && (line?.Id || sessionLine?.Id)) {
        await fetchDetails(line?.Id || sessionLine?.Id);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  // =====================================================================================================================================
  // Entity details (Edit/View) + hydrate transfers from DTO
  // =====================================================================================================================================
  const fetchDetails = async (idToGet) => {
    ctx.setIsLoad(true);
    try {
      const res = await svcUsers.getId(idToGet);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }

      setUserInfo(res || {});
      setUserId(res?.Id);

      // Profiles selection from DTO
      const dtoProfiles = Array.isArray(res?.Profiles)
        ? res.Profiles.map((p) => ({ Id: p.Id, Name: p.Name }))
        : [];
      setUserProfiles(dtoProfiles);

      // Accounts (for View and Edit; Edit can now change this list)
      const dtoAccountIds = Array.isArray(res?.AccountIds) ? res.AccountIds : [];

      if (dtoAccountIds.length > 0) {
        // Start from current accountsRef snapshot
        let rows = accountsRef || [];

        // Detect which account ids are missing from the local reference list
        const missingIds = dtoAccountIds.filter(
          (accId) => !rows.some((r) => Number(r?.Id) === Number(accId))
        );

        // For any missing id, call Accounts/Id to get a full DTO and normalize it
        if (missingIds.length > 0) {
          const fetched = await Promise.all(
            missingIds.map(async (accId) => {
              try {
                const accRes = await svcAccounts.getId(accId);
                if (isFailurePayload(accRes) || !accRes) return null;

                // The GET by id returns shape with lowercase id/name
                const rawId = accRes.id ?? accRes.Id ?? accId;
                const idNum = Number(rawId);
                const name =
                  accRes.name ??
                  accRes.Name ??
                  accRes.displayName ??
                  accRes.DisplayName ??
                  accRes.description ??
                  accRes.Description ??
                  (Number.isFinite(idNum) ? String(idNum) : String(accId));

                return {
                  ...accRes,
                  Id: idNum,
                  Name: name
                };
              } catch {
                return null;
              }
            })
          );

          const validFetched = fetched.filter((x) => x != null);

          if (validFetched.length > 0) {
            // Merge freshly fetched accounts into local rows (no duplicates)
            const byId = new Map(rows.map((a) => [Number(a.Id), a]));
            for (const a of validFetched) {
              byId.set(Number(a.Id), a);
            }
            rows = Array.from(byId.values());
            setAccountsRef(rows);
          }
        }

        // Now build selected accounts using the merged rows
        const selected = dtoAccountIds.map((id) => {
          const numericId = Number(id);
          const found = rows.find((r) => Number(r?.Id) === numericId);

          if (found) {
            return found;
          }

          // Ultimate fallback: id-only label (should happen rarely after fetch by id)
          return {
            Id: numericId,
            Name: String(id)
          };
        });

        setUserAccounts(selected);
      } else {
        setUserAccounts([]);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  // =====================================================================================================================================
  // Paged loaders for Profiles and Accounts
  // =====================================================================================================================================

  /**
   * Paged loader for Profiles (Inactive == false), cautious and idempotent.
   * - Uses a relatively small page size to avoid large payloads.
   * - Iterates while there are more pages indicated by the API response.
   * - Deduplicates by Id and sorts by Name for a stable UX.
   */
  const fetchAllProfilesPaged = async () => {
    const pageSize = 20; // requested smaller page size for Profiles
    let page = 1;
    const acc = [];
    const seen = new Set();

    while (true) {
      const res = await svcProfiles.filter(`?Page=${page}&PageSize=${pageSize}`);
      if (isFailurePayload(res)) break;

      const items = Array.isArray(res?.Items)
        ? res.Items
        : (Array.isArray(res) ? res : []);

      if (!items?.length) break;

      for (const it of items) {
        const id = it?.Id ?? it?.id;
        if (id == null) continue;
        if (it?.Inactive) continue; // skip inactive
        if (seen.has(id)) continue;

        seen.add(id);
        acc.push(it);
      }

      const totalPages = Number(res?.TotalPages ?? 1);
      if (page >= totalPages) break;
      page += 1;
    }

    // Sort by Name (or name) for consistent ordering
    acc.sort((a, b) =>
      String(a?.Name ?? a?.name ?? '').localeCompare(String(b?.Name ?? b?.name ?? ''))
    );

    return acc;
  };

  /**
   * Paged loader for Accounts (Inactive == false), cautious and idempotent.
   * - Iterates across all pages reported by the API.
   * - Deduplicates by Id and normalizes to a stable { Id, Name } shape.
   * - Sorts by Name for a predictable transfer list ordering.
   */
  const fetchAllAccountsPaged = async () => {
    const pageSize = 50; // accounts can use a slightly larger page size
    let page = 1;
    const acc = [];
    const seen = new Set();

    while (true) {
      const res = await svcAccounts.filter(`?Page=${page}&PageSize=${pageSize}`);
      if (isFailurePayload(res)) break;

      const items = Array.isArray(res?.Items)
        ? res.Items
        : (Array.isArray(res) ? res : []);

      if (!items?.length) break;

      for (const it of items) {
        if (it?.Inactive) continue; // skip inactive

        // Try to resolve a numeric Id from common patterns
        const rawId = it.Id ?? it.id ?? it.AccountId ?? it.accountId ?? it.value;
        const id = Number(rawId);
        if (!Number.isFinite(id)) continue;
        if (seen.has(id)) continue;

        seen.add(id);

        // Try to resolve a display name from common patterns
        const name =
          it.Name ??
          it.name ??
          it.DisplayName ??
          it.displayName ??
          it.Description ??
          it.description ??
          String(id);

        acc.push({
          ...it,
          Id: id,
          Name: name
        });
      }

      const totalPages = Number(res?.TotalPages ?? 1);
      if (page >= totalPages) break;
      page += 1;
    }

    // Sort by Name for stable UX
    acc.sort((a, b) =>
      String(a?.Name ?? '').localeCompare(String(b?.Name ?? ''))
    );

    return acc;
  };

  // =====================================================================================================================================
  // Transfer lists reference loaders
  // =====================================================================================================================================
  const loadProfilesRef = async () => {
    try {
      const allProfiles = await fetchAllProfilesPaged();
      setProfilesRef(allProfiles);
    } catch {
      setProfilesRef([]);
    }
  };

  const loadAccountsRef = async () => {
    try {
      const allAccounts = await fetchAllAccountsPaged();
      setAccountsRef(allAccounts);
    } catch {
      setAccountsRef([]);
    }
  };

  // =====================================================================================================================================
  // Validation (schema-based + transfer lists)
  // =====================================================================================================================================
  const validateForm = (values) => {
    const missing = [];
    for (const opt of formSchema) {
      if (!opt?.IsRequired) continue;

      // Never validate internal/derived fields even if backend accidentally flags them
      if (INTERNAL_USER_FIELDS.has(opt.Name)) continue;

      const key = opt.IsForeignKey ? opt.Header : opt.Name;
      const val = values?.[key];
      const isEmpty =
        val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
      if (isEmpty) missing.push(opt.NormalizedName || opt.Name);
    }

    if (isCreateMode) {
      if (!userProfiles?.length) missing.push(t('LabelUserRoles'));
      if (!userAccounts?.length) missing.push(t('Accounts'));
    }

    if (missing.length) {
      toastMessages('error', '', `${t('MandatoryFields')}: ${missing.join(', ')}`);
      return false;
    }
    return true;
  };


  // =====================================================================================================================================
  // Build payload from DF values + transfer lists (preserves original DTO rules)
  // =====================================================================================================================================
  const buildPayloadFromValues = (values) => {
    if (isCreateMode) {
      // Primitive part from DF (skip "Inactive" on create)
      const entries = [];
      for (const opt of formSchema) {
        const keySource = opt.IsForeignKey ? opt.Header : opt.Name;
        const payloadKey = toCamel(keySource || opt.Name);
        const value = values?.[keySource];
        if (opt.Name === 'Inactive') continue;
        if (value !== undefined && value !== '') entries.push([payloadKey, value]);
      }

      const profileIds = (userProfiles || []).map((p) => p.Id);
      const accountIds = (userAccounts || []).map((a) => a.Id);

      // On create we send:
      //  - primitive DF fields (including DefaultAccountId if present in DF),
      //  - profileIds from the Profiles transfer list,
      //  - accountIds from the Accounts transfer list.
      const base = Object.fromEntries(entries);
      return { ...base, profileIds, accountIds };
    }

    // UPDATE: partial semantics.
    // We include:
    //  - any DF fields that have a defined value (including DefaultAccountId when present),
    //  - profileIds when the Profiles transfer list has items,
    //  - accountIds when the Accounts transfer list has items (so Accounts can be updated).
    const entries = [];
    for (const opt of formSchema) {
      const keySource = opt.IsForeignKey ? opt.Header : opt.Name;
      const payloadKey = toCamel(keySource || opt.Name);
      const value = values?.[keySource];
      if (value !== undefined) entries.push([payloadKey, value]);
    }

    if (Array.isArray(userProfiles) && userProfiles.length > 0) {
      entries.push(['profileIds', userProfiles.map((p) => p.Id)]);
    }

    if (Array.isArray(userAccounts) && userAccounts.length > 0) {
      entries.push(['accountIds', userAccounts.map((a) => a.Id)]);
    }

    return Object.fromEntries(entries);
  };

  // =====================================================================================================================================
  // DF submit (single source of truth for Create/Update)
  // =====================================================================================================================================
  const onSubmitDynamic = useCallback(
    async (values) => {
      // Validate before deciding about self-edit flow
      if (!validateForm(values)) return;

      const payload = buildPayloadFromValues(values);

      // Self-edit check
      const isEdit = !isCreateMode;
      const loggedId = getLoggedUserIdFromStorage();
      const editedId = Number(userId);
      const isSelf = isEdit && loggedId != null && Number.isFinite(editedId) && loggedId === editedId;

      // If user is editing himself, or setting himself inactive, require confirm & re-login
      const selfBecameInactive = isSelf && !!payload?.inactive;
      if (isSelf || selfBecameInactive) {
        setPendingSelfUpdatePayload(payload);
        setShowSelfReauthConfirm(true);
        return;
      }

      // Normal flow (create or editing other user)
      ctx.setIsLoad(true);
      try {
        const res = isCreateMode
          ? await svcCreate.post(payload)
          : await svcUpdate.update(userId, payload);

        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }

        toastMessages('success', '', isCreateMode ? t('UserCreated') : t('UserUpdated'));
        handleTabClose(id);
      } catch (err) {
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [formSchema, isCreateMode, userProfiles, userAccounts, userId, ctx, t, toastMessages, handleTabClose, id]
  );

  // =====================================================================================================================================
  // Trigger DF submit via Save button
  // =====================================================================================================================================
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  // =====================================================================================================================================
  // Refresh Options and, if applicable, Details (FKs are not loaded for DF)
  // =====================================================================================================================================
  const onRefresh = () => {
    fetchOptions(operation, sessionLine);
  };

  // =====================================================================================================================================
  // Lightweight state persistence on DF field changes (patch by field)
  // =====================================================================================================================================
  const handleFormValuesChange = useCallback(
    (patch /* { [name]: value } */, meta) => {
      if (!patch || !id) return;

      const prevTabState = M_State.find((o) => o.key === id)?.value || [];
      const pick = (k) => prevTabState.find((x) => x.key === k)?.value;
      const merged = { ...(pick('userInfo') || userInfo || {}), ...patch };

      const updated = [
        ...prevTabState.filter((x) => x.key !== 'userInfo'),
        { key: 'userInfo', value: merged }
      ];
      dispatch(M_StateActions.addM_State({ key: id, value: updated }));
    },
    [M_State, id, userInfo, dispatch]
  );

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t('RefreshTable')}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={onRefresh}
              label={t('RefreshTable')}
            />
            {!isViewMode && (
              <MButton
                tooltip={t('Save')}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t('Save')}
              />
            )}
          </>
        }
      />

      <div
        style={{
          padding: '20px',
        }}
      >
        {/* =====================================================================================================================================
            OUTER CARD CONTAINER
           ===================================================================================================================================== */}
        <div className="firstDivContainer">
          <div className="user-config-layout">
            {/* =====================================================================================================================================
        DYNAMIC FORM
        NOTE: FK resolver removed; include rules exclude FK fields so DF does not need FK datasets here,
              except for DefaultAccountId on Edit which is intentionally allowed.
       ===================================================================================================================================== */}
            <div className="user-config-form">
              <DynamicForm
                key={`df-user-${id}-${operation}-${userId ?? 'new'}`}
                formRef={formRef}
                mode={(operation || 'view').toLowerCase()}
                schema={allOptions}
                data={userInfo}
                onSubmit={onSubmitDynamic}
                onValuesChange={handleFormValuesChange}
                includeInCreate={includeInCreate}
                includeInEdit={includeInEdit}
                includeInView={includeInView}
                title={t('UserInformation')}
                containerClassName="alignInputsUserForm"
                align="vertical"
              />
            </div>

            {/* =============================================================================================================================================
        TRANSFER LISTS (Profiles + Accounts)
        Desktop: side-by-side with the form
        Mobile: stacked (CSS media query)
       =========================================================================================================================================== */}

            {/* PROFILES (always shown; disabled on View) */}
            <div className="user-config-transfer">
              <MTransferList
                disabled={isViewMode}
                header={t('LabelUserRoles')}
                entity="Profiles"
                field="Name"
                valueIdField="Id"
                source={profilesRef}
                value={userProfiles}
                setValue={setUserProfiles}
              />
            </div>

            {/* ACCOUNTS (Create + Edit + View; disabled on View) */}
            {(isCreateMode || isEditMode || isViewMode) && (
              <div className="user-config-transfer">
                <MTransferList
                  disabled={isViewMode}
                  header={t('Accounts')}
                  entity="Accounts"
                  field="Name"
                  valueIdField="Id"
                  source={accountsRef}
                  value={userAccounts}
                  setValue={setUserAccounts}
                />
              </div>
            )}
          </div>
        </div>

      </div>

      {/* =========================================================================================================================================================
           Confirmation dialog ONLY for self-edit BEFORE committing changes.
         ========================================================================================================================================================= */}
      <MMessageBoard
        title={t('ConfirmReLogin') || 'Confirm re-login'}
        open={showSelfReauthConfirm}
        onClose={() => {
          setShowSelfReauthConfirm(false);
          setPendingSelfUpdatePayload(null);
        }}
        warning
        body={
          <div style={{ fontSize: '16px', padding: '10px' }}>
            {t('YouWillNeedToLoginAgainToApplyChanges') ||
              'You have edited your own user. A new login is required to apply these changes. Do you want to continue?'}
          </div>
        }
        footer={
          <>
            <MButton
              label={t('Cancel') || 'Cancel'}
              backgroundColor={'var(--color-red)'}
              labelColor={'var(--color-white)'}
              icon={<i className="pi pi-times iconTable" style={{ color: 'white' }}></i>}
              onClick={() => {
                setShowSelfReauthConfirm(false);
                setPendingSelfUpdatePayload(null);
              }}
            />
            <MButton
              label={t('Confirm') || 'Confirm'}
              onClick={confirmSelfReauthAndSave}
              backgroundColor={'var(--color-green)'}
              labelColor={'var(--color-white)'}
              icon={<i className="pi pi-check iconTable" style={{ color: 'white' }}></i>}
            />
          </>
        }
        minWidth={420}
        maxWidth={560}
      />
    </>
  );
}

UserConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
